/***
 *
 *   ONBOARDING
 *   Phase 13 — signup-wizard chrome (ProgressSteps + AuthPage + Back /
 *   Continue / Skip / Finish). Keeps live step progression via
 *   /api/account/step and onboarded flag.
 *
 *   PROPS
 *   views: array of { name, description, component, isComplete, icon?, skippable? }
 *   save: patch user.onboarded on finish
 *   onFinish: navigate target after finish
 *
 **********/

import { useState, useEffect, useContext } from 'react';
import {
    Animate,
    Button,
    ProgressSteps,
    AuthPage,
    useNavigate,
} from 'components/lib';
import Style from './onboarding.tailwind.js';
import { useApiContext } from 'app/api.js';
import { AuthContext } from 'app/auth';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslate } from 'app/translations.js';

export function Onboarding(props) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { _t } = useTranslate();
    const { postEvent, callApi } = useApiContext();
    const { setUserOnboarded } = useContext(AuthContext);
    const user = useQuery('user', () => callApi({ url: '/api/user' }));

    const account = user.data?.data.accounts.find(
        (a) => a?.id === user?.data.data.account_id,
    );

    const [activeView, setActiveView] = useState(0);
    const [view, setView] = useState(props.views[activeView]);

    const { mutate: updateStep } = useMutation(
        async (step) => {
            await callApi({
                method: 'put',
                url: '/api/account/step',
                data: { step },
                headers: {
                    'Content-Type': 'application/json',
                },
            });
        },
        {
            onSuccess: (data, variables) => {
                setActiveView(variables);
                queryClient.invalidateQueries('auth');
            },
        },
    );

    const { mutate: finish, isLoading: finishing } = useMutation(
        async () => {
            if (props.save)
                await callApi({
                    method: 'patch',
                    url: '/api/user',
                    data: { onboarded: true },
                });
        },
        {
            onSuccess: () => {
                setUserOnboarded(true);
                navigate(props.onFinish || '/app/tickets');
            },
        },
    );

    useEffect(() => {
        if (account) {
            setActiveView(account.setup_step);
        }
    }, [account]);

    useEffect(() => {
        setView(props.views[activeView]);
    }, [activeView, props.views]);

    if (!view) return false;

    function goTo(viewIndex) {
        const activeViewParams = props.views[activeView];
        if (!activeViewParams.isComplete && viewIndex > activeView) return;

        window.location.hash = viewIndex + 1;
        setActiveView(viewIndex);
    }

    function next() {
        const viewIndex = activeView + 1;
        window.location.hash = viewIndex + 1;
        updateStep(viewIndex);
    }

    function complete() {
        const viewIndex = activeView + 1;
        updateStep(viewIndex);

        setView((prev) => ({ ...prev, isComplete: true }));
    }

    function skip() {
        next();
    }

    const isLast = activeView === props.views.length - 1;
    const canContinue = view.isComplete && !isLast;
    const showSkip = view.skippable && !isLast;

    const stepItems = props.views.map((v, i) => ({
        key: v.key || `step-${i}`,
        name: v.name,
        icon: v.icon,
        completed: i < activeView || (i === activeView && v.isComplete && !isLast),
    }));

    return (
        <Animate type="pop">
            <div className={Style.stack}>
                <ProgressSteps items={stepItems} current={activeView} />

                <AuthPage title={view.name} description={view.description}>
                    <view.component
                        next={next}
                        complete={complete}
                        finish={finish}
                        skip={skip}
                        isComplete={view.isComplete}
                        account={account}
                    />
                </AuthPage>

                <nav className={Style.nav}>
                    {activeView > 0 && (
                        <Button
                            variant="secondary"
                            icon="arrow-left"
                            alignLeft
                            text={_t('previous')}
                            className={Style.navBtn}
                            action={() => goTo(activeView - 1)}
                        />
                    )}

                    {showSkip && (
                        <Button
                            variant="ghost"
                            text={_t('onboarding.skip')}
                            className={Style.navBtn}
                            action={skip}
                        />
                    )}

                    {canContinue && (
                        <Button
                            variant="primary"
                            icon="arrow-right"
                            alignRight
                            text={_t('next')}
                            className={Style.navBtn}
                            action={() => goTo(activeView + 1)}
                        />
                    )}

                    {isLast && (
                        <Button
                            variant="primary"
                            icon="check"
                            alignRight
                            text={_t('onboarding.finish')}
                            className={Style.navBtn}
                            isLoading={finishing}
                            action={() => {
                                postEvent('completed_onboarding');
                                finish();
                            }}
                        />
                    )}
                </nav>
            </div>
        </Animate>
    );
}
